import { useState, useMemo } from 'react'
import { motion } from 'framer-motion'
import { Link, useNavigate } from 'react-router-dom'
import { FigmaPartCard } from '@/components/ui/figma-part-card'
import { AuthModal } from '@/components/common'
import { appRoutes } from '@/constants'
import { useParts } from '@/modules/parts/hooks/use-parts'
import { Loader2, PackageOpen, Sparkles, ArrowRight } from 'lucide-react'
import { Button } from '@/components/ui/button'

export function FeaturedPartsSection() {
  const [authModalOpen, setAuthModalOpen] = useState(false)
  const [wishlistedIds, setWishlistedIds] = useState<string[]>([])
  const [activeSlide, setActiveSlide] = useState(0)
  const navigate = useNavigate()

  // Fetch only parts where isFeatured is true
  const { data: partsData, isLoading } = useParts({ isFeatured: true, limit: 12 })

  // Strict check: only parts marked as featured by admin
  const featuredParts = useMemo(() => {
    const list = partsData?.items || []
    return list.filter((p) => p.isFeatured)
  }, [partsData])

  const handleToggleWishlist = (id: string) => {
    if (wishlistedIds.includes(id)) {
      setWishlistedIds(wishlistedIds.filter((item) => item !== id))
    } else {
      setWishlistedIds([...wishlistedIds, id])
    }
  }

  const handleSelectPart = (idOrSlug: string) => {
    navigate(`${appRoutes.parts}/${idOrSlug}`)
  }

  const formatCurrency = (val: any) => {
    if (val === null || val === undefined) return '₹0'
    const num = typeof val === 'number' ? val : Number(String(val).replace(/[^0-9.]/g, ''))
    if (isNaN(num)) return '₹0'
    return `₹${num.toLocaleString('en-IN')}`
  }

  return (
    <>
      <section id="featured-parts" className="py-12 bg-background relative border-b border-border/60">
        <div className="mx-auto max-w-[1440px] px-4 sm:px-6 lg:px-8 space-y-6">
          {/* Heading row */}
          <div className="flex items-end justify-between border-b border-border/60 pb-4">
            <div>
              <div className="flex items-center gap-1.5 text-xs font-bold text-[#CF2F2F] uppercase tracking-wider mb-1">
                <Sparkles className="size-3.5" /> Handpicked By Curators
              </div>
              <h2
                style={{
                  fontFamily: "'DM Sans', 'Poppins', sans-serif",
                  fontWeight: 800,
                  fontSize: '22px',
                  letterSpacing: '-0.01em',
                  color: 'var(--color-text-primary)',
                }}
              >
                FEATURED <span style={{ color: '#CF2F2F' }}>SPARE PARTS</span>
              </h2>
              <p
                style={{
                  fontFamily: "'DM Sans', sans-serif",
                  fontSize: '13px',
                  color: 'var(--color-text-secondary)',
                  marginTop: '4px',
                }}
              >
                Admin-selected performance assemblies and verified OEM components.
              </p>
            </div>
            <Link
              to={appRoutes.parts}
              style={{
                fontFamily: "'DM Sans', sans-serif",
                fontWeight: 700,
                fontSize: '12px',
                color: '#CF2F2F',
                textDecoration: 'none',
                letterSpacing: '0.04em',
                textTransform: 'uppercase',
              }}
              className="inline-flex items-center gap-1 hover:underline cursor-pointer"
            >
              <span>VIEW ALL PARTS</span>
              <ArrowRight className="size-3.5" />
            </Link>
          </div>

          {/* Dynamic Content */}
          {isLoading ? (
            <div className="p-16 text-center flex flex-col items-center justify-center space-y-3">
              <Loader2 className="size-8 animate-spin text-primary" />
              <p className="text-sm text-muted-foreground">Loading featured parts...</p>
            </div>
          ) : featuredParts.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-border bg-card/60 p-10 text-center flex flex-col items-center justify-center space-y-3">
              <div className="size-12 rounded-xl bg-primary/10 text-primary flex items-center justify-center">
                <PackageOpen className="size-6" />
              </div>
              <h3 className="font-display text-base font-bold text-foreground">
                No Featured Parts Selected Yet
              </h3>
              <p className="text-xs text-muted-foreground max-w-sm">
                Parts toggled as "Featured" in the Admin Parts Panel will appear right here on the homepage.
              </p>
              <Button asChild size="sm" variant="outline" className="text-xs font-bold mt-2">
                <Link to={appRoutes.parts}>Browse Parts Catalog</Link>
              </Button>
            </div>
          ) : (
            <>
              {/* Cards Grid — strictly shows only admin-featured parts */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                {featuredParts.map((part, index) => {
                  const img = Array.isArray(part.images) && part.images.length > 0
                    ? (typeof part.images[0] === 'string' ? part.images[0] : (part.images[0] as any)?.url || '')
                    : 'https://images.unsplash.com/photo-1486006920555-c77dce18193b?auto=format&fit=crop&w=600&q=80'

                  const priceStr = formatCurrency(part.price)
                  const unitPrice = typeof part.price === 'number' ? part.price : Number(String(part.price).replace(/[^0-9.]/g, '')) || 0
                  const originalPriceStr = unitPrice > 0 ? `₹${Math.round(unitPrice * 1.15).toLocaleString('en-IN')}` : undefined
                  const specText = `${part.condition || 'Brand New'} · ${part.brand}`

                  return (
                    <motion.div
                      key={part.id}
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.35, delay: index * 0.06 }}
                    >
                      <FigmaPartCard
                        id={part.slug || String(part.id)}
                        title={part.title}
                        specText={specText}
                        price={priceStr}
                        originalPrice={originalPriceStr}
                        image={img}
                        isOem={Boolean(part.isOEM)}
                        isWishlisted={wishlistedIds.includes(String(part.id))}
                        onToggleWishlist={() => handleToggleWishlist(String(part.id))}
                        onSelect={() => handleSelectPart(part.slug || String(part.id))}
                      />
                    </motion.div>
                  )
                })}
              </div>

              {/* Slide indicators if multiple cards */}
              {featuredParts.length > 4 && (
                <div className="flex items-center justify-center gap-2 pt-2">
                  {Array.from({ length: Math.ceil(featuredParts.length / 4) }).map((_, idx) => (
                    <button
                      key={idx}
                      type="button"
                      onClick={() => setActiveSlide(idx)}
                      style={{
                        backgroundColor: idx === activeSlide ? '#CF2F2F' : '#94a3b8',
                        width: idx === activeSlide ? '24px' : '8px',
                        height: '8px',
                        borderRadius: '9999px',
                        transition: 'all 0.3s',
                        border: 'none',
                        cursor: 'pointer',
                        padding: 0,
                      }}
                      aria-label={`Go to slide ${idx + 1}`}
                    />
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </section>

      <AuthModal
        open={authModalOpen}
        onOpenChange={setAuthModalOpen}
        initialMode="login"
        title="Sign In to Order Parts"
        description="Sign in or register to place your order directly with verified OEM part suppliers."
      />
    </>
  )
}
